import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Loader2, Send, UserPlus, X } from 'lucide-react';
import { byopApi, type ByopUitnodiging } from '../services/byopApi';

/**
 * Bring Your Own Professional (migratie 164).
 *
 * De opdrachtgever nodigt eigen, nog niet op Belagom geregistreerde
 * professionals uit. Na registratie en screening zijn zij openbaar boekbaar
 * voor iedereen; deze opdrachtgever betaalt bij hen een lagere platform-opslag
 * (5% → 10% → 15%). De professional houdt altijd 100% van zijn tarief.
 */

const STATUS_LABEL: Record<string, { tekst: string; klasse: string }> = {
  PENDING_REGISTRATION: { tekst: 'Uitgenodigd', klasse: 'bg-amber-50 text-amber-800 border-amber-200' },
  EXPIRED: { tekst: 'Link verlopen', klasse: 'bg-gray-100 text-gray-600 border-gray-200' },
  REVOKED: { tekst: 'Ingetrokken', klasse: 'bg-gray-100 text-gray-500 border-gray-200' },
  REGISTERED: { tekst: 'Geregistreerd', klasse: 'bg-blue-50 text-blue-800 border-blue-200' },
};

function fase(u: ByopUitnodiging): { tekst: string; klasse: string } {
  if (u.status !== 'REGISTERED') return STATUS_LABEL[u.status] ?? STATUS_LABEL.EXPIRED;
  if (!u.byop_start_date) return { tekst: 'In screening', klasse: 'bg-blue-50 text-blue-800 border-blue-200' };
  return { tekst: 'Actief', klasse: 'bg-green-50 text-green-800 border-green-200' };
}

const datum = (v: string | null) => (v ? new Date(v).toLocaleDateString('nl-NL') : '—');

export const EmployerByopPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '' });

  const { data: uitnodigingen = [], isLoading } = useQuery({ queryKey: ['byop'], queryFn: byopApi.overzicht });

  const fout = (err: any) => toast.error(err?.response?.data?.message || 'Er is iets misgegaan');

  const nodigUit = useMutation({
    mutationFn: byopApi.nodigUit,
    onSuccess: () => {
      toast.success('Uitnodiging verstuurd');
      setForm({ email: '', first_name: '', last_name: '' });
      queryClient.invalidateQueries({ queryKey: ['byop'] });
    },
    onError: fout,
  });

  const trekIn = useMutation({
    mutationFn: byopApi.trekIn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['byop'] }),
    onError: fout,
  });

  const opnieuw = (u: ByopUitnodiging) =>
    nodigUit.mutate({ email: u.email, first_name: u.first_name ?? undefined, last_name: u.last_name ?? undefined });

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Eigen professional uitnodigen</h1>
        <p className="text-gray-600 mt-1">
          Breng de professionals mee met wie u al werkt. Zij doorlopen dezelfde screening als iedere professional op
          BeLagom en zijn daarna zichtbaar en boekbaar voor het hele platform.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-gray-700">
            <p className="font-medium text-gray-900 mb-2">Lagere platform-opslag voor uw eigen professionals</p>
            <table className="text-sm">
              <tbody>
                <tr><td className="pr-6 py-0.5">Maand 1 t/m 6</td><td className="font-semibold">5%</td></tr>
                <tr><td className="pr-6 py-0.5">Maand 7 t/m 12</td><td className="font-semibold">10%</td></tr>
                <tr><td className="pr-6 py-0.5">Vanaf maand 13</td><td className="font-semibold">15% (standaard)</td></tr>
              </tbody>
            </table>
            <p className="mt-2 text-gray-500">
              De klok start in de maand waarin de screening is goedgekeurd. De opslag komt bovenop het tarief van de
              professional; de professional ontvangt altijd 100% van zijn tarief. Uitnodigen kan alleen voor
              professionals die nog geen BeLagom-account hebben.
            </p>
          </div>
        </div>
      </div>

      <form
        className="bg-white border border-gray-200 rounded-lg p-5 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          nodigUit.mutate({
            email: form.email.trim(),
            first_name: form.first_name.trim() || undefined,
            last_name: form.last_name.trim() || undefined,
          });
        }}
      >
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <UserPlus className="w-5 h-5" /> Nieuwe uitnodiging
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Voornaam"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          />
          <input
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Achternaam"
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          />
          <input
            type="email"
            required
            className="px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="E-mailadres"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">De professional ontvangt een e-mail met een activatielink die 72 uur geldig is.</p>
          <button
            type="submit"
            disabled={nodigUit.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            {nodigUit.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Uitnodigen
          </button>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        {isLoading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : uitnodigingen.length === 0 ? (
          <p className="p-6 text-sm text-gray-500">Nog geen uitnodigingen verstuurd.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-2 font-medium">Professional</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Uitgenodigd</th>
                <th className="px-4 py-2 font-medium">Huidige opslag</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {uitnodigingen.map((u) => {
                const f = fase(u);
                const naam = [u.first_name, u.last_name].filter(Boolean).join(' ');
                return (
                  <tr key={u.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{naam || u.email}</div>
                      {naam && <div className="text-gray-500">{u.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${f.klasse}`}>{f.tekst}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{datum(u.created_at)}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {u.huidige_fee_percentage != null
                        ? `${u.huidige_fee_percentage}% (maand ${u.huidige_maand}, sinds ${datum(u.byop_start_date)})`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {u.status === 'PENDING_REGISTRATION' && (
                        <button
                          onClick={() => trekIn.mutate(u.id)}
                          className="inline-flex items-center gap-1 text-gray-500 hover:text-red-600"
                          title="Uitnodiging intrekken"
                        >
                          <X className="w-4 h-4" /> Intrekken
                        </button>
                      )}
                      {(u.status === 'EXPIRED' || u.status === 'REVOKED') && (
                        <button onClick={() => opnieuw(u)} className="text-blue-600 hover:text-blue-700">
                          Opnieuw uitnodigen
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default EmployerByopPage;
